import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, CircularProgress, LinearProgress, Typography } from '@mui/material';
import ChapterAccordion from './components/ChapterAccordion';
import LessonPlayer from './components/LessonPlayer';
import LessonSteps from './components/LessonSteps';
import useTutorialChapters from './useTutorialChapters';
import useTutorialProgress, { countCompleted, isLessonCompleted } from './useTutorialProgress';
import styles from './Tutorials.module.css';

const DESKTOP_MIN_WIDTH = 1100;

/**
 * Página de tutoriais: capítulos e lições em sanfona à esquerda, vídeo no centro e passo a passo à direita.
 * Dados em /data/tutorials.json: { chapters: [{ id, number, title, description, lessons: [...] }] }.
 */
const Tutorials = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedLessonId = searchParams.get('licao');
  const playerRef = useRef(null);
  const { chapters, loading, error: loadError } = useTutorialChapters();
  const { progressById, loaded: progressLoaded, track } = useTutorialProgress();
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Cada lição escolhida (clique, setas ou contagem do fim do vídeo) reinicia e toca o vídeo
  const [playToken, setPlayToken] = useState(0);
  const error = loadError ? 'Não foi possível carregar os tutoriais. Por favor, tente novamente mais tarde.' : null;

  // Abre na lição do link (?licao=<id>, usado nos e-mails) ou na primeira ainda não assistida
  // (ou no começo, se a pessoa já viu tudo)
  useEffect(() => {
    if (selectedChapterId !== null || chapters.length === 0 || !progressLoaded) return;
    for (const chapter of chapters) {
      const linked = linkedLessonId ? (chapter.lessons || []).findIndex((item) => item.id === linkedLessonId) : -1;
      if (linked >= 0) {
        setSelectedChapterId(chapter.id);
        setCurrentIndex(linked);
        return;
      }
    }
    for (const chapter of chapters) {
      const pending = (chapter.lessons || []).findIndex((item) => !isLessonCompleted(progressById[item.id]));
      if (pending >= 0) {
        setSelectedChapterId(chapter.id);
        setCurrentIndex(pending);
        return;
      }
    }
    setSelectedChapterId(chapters[0].id);
  }, [chapters, selectedChapterId, progressLoaded, progressById, linkedLessonId]);

  const allLessons = chapters.flatMap((chapter) => chapter.lessons || []);
  const completedCount = countCompleted(allLessons, progressById);

  const selectedChapter = chapters.find((chapter) => chapter.id === selectedChapterId) || null;
  const lessons = selectedChapter?.lessons || [];
  const lesson = lessons[currentIndex] || null;

  // No celular o menu fica abaixo do vídeo; escolher uma lição sobe até o player
  const scrollToPlayer = useCallback(() => {
    if (window.innerWidth < DESKTOP_MIN_WIDTH && playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Lição escolhida no menu lateral, de qualquer capítulo
  const selectLesson = (chapterId, index) => {
    setSelectedChapterId(chapterId);
    setCurrentIndex(index);
    setPlayToken((token) => token + 1);
    scrollToPlayer();
  };

  const goToLesson = useCallback((index) => {
    if (index < 0 || index >= lessons.length) return;
    setCurrentIndex(index);
    setPlayToken((token) => token + 1);
    scrollToPlayer();
  }, [lessons.length, scrollToPlayer]);

  const goToNext = useCallback(() => goToLesson(currentIndex + 1), [goToLesson, currentIndex]);

  // Setas do teclado trocam de lição (exceto quando o foco está no vídeo ou em um campo)
  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'VIDEO') return;
      if (event.key === 'ArrowRight') goToLesson(currentIndex + 1);
      if (event.key === 'ArrowLeft') goToLesson(currentIndex - 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentIndex, goToLesson]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Tutoriais</h1>
          {progressLoaded && allLessons.length > 0 && (
            <div className={styles.overallProgress}>
              <Typography sx={{ fontSize: '0.8rem', color: '#4A5568', whiteSpace: 'nowrap' }}>
                {completedCount} de {allLessons.length} lições assistidas
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.round((completedCount / allLessons.length) * 100)}
                sx={{ width: 120, height: 6, borderRadius: 3 }}
              />
            </div>
          )}
        </div>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <div className={styles.centered}>
          <CircularProgress size={32} />
          <Typography sx={{ fontSize: '0.95rem' }}>Carregando tutoriais...</Typography>
        </div>
      )}

      {!loading && !error && chapters.length === 0 && (
        <div className={styles.centered}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Nenhum tutorial disponível no momento.</Typography>
        </div>
      )}

      {!loading && chapters.length > 0 && (
        <>
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <ChapterAccordion
                chapters={chapters}
                selectedChapterId={selectedChapterId}
                currentIndex={currentIndex}
                onSelect={selectLesson}
                progressById={progressById}
              />
            </aside>
            <div ref={playerRef} className={styles.player}>
              {lesson && (
                <LessonPlayer
                  lesson={lesson}
                  index={currentIndex}
                  total={lessons.length}
                  onPrev={() => goToLesson(currentIndex - 1)}
                  onNext={goToNext}
                  onOpenScreen={(route) => navigate(route)}
                  nextTitle={lessons[currentIndex + 1]?.title}
                  playToken={playToken}
                  autoPlay={playToken > 0}
                  progress={progressById[lesson.id] || null}
                  onTrack={track}
                  source="page"
                  showSteps={false}
                />
              )}
            </div>
            <div className={styles.steps}>
              {lesson && <LessonSteps lesson={lesson} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Tutorials;
